import React, { useEffect, useRef, useState } from 'react'
import { VideoQuestionInternalViewModelTypes } from './types'

import useSurveyLog from '../../modules/survey_log/useSurveyLog'

import style from './style.module.scss'
import DevLog from '@aytm/app/swidget/base/modules/devlog/Module.ts'

const VideoInternalView = ({ schema, onVideoViewed, onVideoLoaded, stopped }: VideoQuestionInternalViewModelTypes) => {
  const [disableVideo, setDisableVideo] = useState<boolean>(false)
  const [fadeVideo, setFadeVideo] = useState<boolean>(false)
  const [counterTime, setCounterTime] = useState<number>(0)

  const videoRef = useRef<HTMLVideoElement>()
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const videoTimeRef = useRef<number>(0)

  const surveyLog = useSurveyLog()

  const video = schema.video
  const duration = video.duration
  const minDuration = schema.min_duration ? Math.min(schema.min_duration, duration) : duration

  const isStopped = useRef(stopped)
  if (isStopped.current !== stopped) {
    isStopped.current = stopped
    if (stopped) {
      videoRef.current?.pause()
    }
  }

  const runTimer = () => {
    if (null === timerRef.current) {
      timerRef.current = setInterval(() => {
        if (!videoRef.current?.seeking && !videoRef.current?.paused) setCounterTime(prev => prev + 1)
      }, 1000)
    }
  }

  useEffect(() => {
    runTimer()
    return () => clearInterval(timerRef.current)
  }, [])

  useEffect(() => {
    if (schema.play_only_once && counterTime == Math.floor(duration) - 1) {
      setDisableVideo(true)
    }
    if (minDuration * 0.98 - 1 < counterTime) {
      onVideoViewed(schema.video.uid)

      if (videoRef.current?.currentTime < minDuration * 0.9 - 1) {
        // send video survey log
        const log = {
          message: `SWidget VO Video Time Error: video current time - ${videoRef.current?.currentTime}, video min duration - ${minDuration}`,
          qref: schema.id
        }
        void surveyLog.log(log)
      }
    }
  }, [counterTime, minDuration, duration, schema.play_only_once, schema.id, onVideoViewed, surveyLog, schema.video.uid])

  const videoPlay = () => {
    const delta = videoRef.current.currentTime - videoTimeRef.current
    if (Math.abs(delta) > 0.02) {
      videoRef.current.currentTime = videoTimeRef.current
    }
    if (counterTime - videoTimeRef.current > 0.8) {
      setCounterTime(Math.round(videoTimeRef.current))
    }
  }

  const videoEnded = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else if (document.webkitFullscreenElement) {
      void document.webkitExitFullscreen()
    }
    if (disableVideo) {
      setFadeVideo(true)
    }
    videoTimeRef.current = 0
  }

  const videoTimeupdate = () => {
    if (!videoRef.current?.seeking && !videoRef.current?.paused) {
      videoTimeRef.current = videoRef.current.currentTime
    }
  }

  const onError = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    DevLog.log('VideoAssetError', event.currentTarget.src, event.currentTarget.error)
  }

  const onStalled = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    DevLog.log('VideoAssetError onStalled', event.currentTarget.src)
  }

  const onSuspend = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    DevLog.log('VideoAssetError onSuspend', event.currentTarget.src)
  }

  const onAbort = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    DevLog.log('VideoAssetError onAbort', event.currentTarget.src)
  }

  return (
    <div className={`${style.videoContainer} ${disableVideo ? style.disabled : ''} ${fadeVideo ? style.faded : ''}`}>
      <video
        poster={video.thumbnail || ''}
        ref={videoRef}
        controls
        controlsList="nodownload noplaybackrate"
        onContextMenu={event => event.preventDefault()}
        onPlay={videoPlay}
        onEnded={videoEnded}
        onError={onError}
        onStalled={onStalled}
        onSuspend={onSuspend}
        onAbort={onAbort}
        onLoadedData={onVideoLoaded}
        onTimeUpdate={videoTimeupdate}
        data-testid="SW-VO-Video"
      >
        <source src={video.url} type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
      </video>
    </div>
  )
}

export default VideoInternalView
